<template>
  <b-dropdown
    variant="link"
    toggle-class="text-decoration-none outline-none shadow-none text-primary p-0 text-medium mw-0"
    menu-class="menu-shadow"
    boundary="viewport"
    class="d-flex"
    :disabled="disabled"
    no-caret
    v-if="showDropdown"
  >
    <template slot="button-content">
      <div
        class="field d-flex align-items-center"
        :class="{ sorted: sortedBy === columnKey, [alignmentClass]: true }"
      >
        <span class="dot" title="Filter applied" v-if="isFilterApplied"></span>
        <span
          class="header-title text-truncate"
          :class="{ sorted: sortedBy === columnKey }"
          :title="title"
          >{{ title }}</span
        >
        <template v-if="sortedBy === columnKey">
          <i class="arrow-down-icon" v-if="sortedDirection === 'descending'"></i>
          <i class="arrow-up-icon" v-else></i>
        </template>
      </div>
    </template>
    <template v-if="showSortOptions">
      <b-dropdown-item
        class="text-primary text-medium"
        v-for="option in sortOptions"
        :key="option.title"
        @click="$emit('sort', { by: columnKey, direction: option.direction })"
        :active="sortedBy === columnKey && sortedDirection === option.direction"
        >{{ option.title }}</b-dropdown-item
      >
    </template>
    <b-dropdown-item
      class="text-primary text-medium"
      @click="$emit('reset')"
      v-if="showSortOptions && sortedBy === columnKey"
      >Clear</b-dropdown-item
    >
    <b-dropdown-divider
      v-if="showSortOptions && showFilterOptions && filterOptions.length > 0"
    ></b-dropdown-divider>
    <template v-if="showFilterOptions">
      <div
        class="dropdown-item text-medium"
        v-for="option in filterOptions"
        :key="option.id"
        @click="
          $emit('filter', {
            filterValue: option.id,
            selected: Number(!isFilterParamSelected(option.id))
          })
        "
      >
        <div class="d-flex align-items-center justify-content-start" @click.stop>
          <b-form-checkbox
            @change="
              $emit('filter', {
                filterValue: option.id,
                selected: Number(!isFilterParamSelected(option.id))
              })
            "
            :checked="isFilterParamSelected(option.id)"
          >
            {{ option.label }}
          </b-form-checkbox>
        </div>
      </div>
    </template>
    <b-dropdown-divider v-if="showFilterOptions && showFieldSettings"></b-dropdown-divider>
    <b-dropdown-item
      class="text-primary text-medium"
      v-for="option in fieldSettingOptions"
      :key="option.id"
      @click="$emit('field-setting', option)"
      >{{ option.title }}</b-dropdown-item
    >
  </b-dropdown>
  <div class="d-flex" :class="{ [alignmentClass]: true }" v-else>
    {{ title }}
  </div>
</template>

<script>
import * as R from 'ramda'
import { BDropdownItem, BDropdown, BDropdownDivider, BFormCheckbox } from 'bootstrap-vue'

/**
 * This is the dropdown used for sorting options over Table
 * @emit
 * - sort: Emitted when sort is applied
 *    - params: {by, direction}
 * - reset: emitted when sort is reset
 */
export default {
  name: 'SortDropdown',
  props: {
    sortedBy: {},
    sortedDirection: {},
    columnKey: {},
    title: {},
    sortOptions: {},
    filterOptions: { default: () => [] },
    filteredBy: { default: () => ({}) },
    showFilterOptions: { default: true },
    showSortOptions: Boolean,
    showFieldSettings: Boolean,
    fieldSettingOptions: { default: () => [] },
    /**
     * supported values - left, center
     */
    align: { default: 'center' },
    disabled: { default: false, type: Boolean }
  },
  components: { BDropdownItem, BDropdown, BDropdownDivider, BFormCheckbox },
  computed: {
    showDropdown() {
      return this.showSortOptions || this.showFilterOptions || this.showFieldSettings
    },
    isFilterApplied() {
      const unselectedParams = R.pickBy(val => val === 0, this.filteredBy)
      return (
        !R.isEmpty(unselectedParams) &&
        (Object.keys(unselectedParams).length < this.filterOptions?.length ||
          !R.isNil(unselectedParams.blank))
      )
    },
    alignmentClass() {
      const alignmentClassMap = {
        left: 'justify-content-start',
        center: 'justify-content-center'
      }
      return alignmentClassMap[this.align]
    }
  },
  methods: {
    isFilterParamSelected(filterParam) {
      const { filteredBy = {} } = this
      return filteredBy[filterParam] !== 0
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

/deep/ .arrow-down-icon,
/deep/ .arrow-up-icon {
  width: 12px;
  height: 9px;
  background-size: contain;
  margin-left: 2px;
}
.sorted {
  color: $primary;
  font-weight: 600;
}
.hidden {
  visibility: hidden;
}
.dot {
  width: 5px;
  height: 5px;
  display: block;
  background: $brand;
  border-radius: 50%;
  position: absolute;
  left: -8px;
}
.field {
  &:not(.sorted):after {
    content: ' ';
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid $prime;
    margin-left: 2px;
    margin-top: 2px;
    visibility: hidden;
  }
  &:hover:after {
    visibility: visible;
  }
}
</style>
